import type { HTMLAttributes, ReactNode } from 'react';
import { Fragment } from 'react';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faWarehouse } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { clsx } from 'clsx';

import { formatRelativeDate } from '@spacefill/shared/src/utils/Date';

import { Avatar } from '../Avatar';
import type { AvatarProps } from '../Avatar/Avatar';
import { Divider } from '../Divider';

export interface InAppNotificationProps {
    title: string;
    titleIcon: IconDefinition;
    warehouseName?: string;
    user: AvatarProps['user'];
    receivedAt: Date;
    unread?: boolean;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLDivElement>;
    children: ReactNode;
}

export const InAppNotification = ({
    title,
    titleIcon,
    warehouseName,
    user,
    receivedAt,
    unread = true,
    dataTestid,
    htmlAttributes,
    children,
}: InAppNotificationProps) => (
    <div
        data-testid={dataTestid}
        {...htmlAttributes}
        className={clsx('rounded-xl bg-white px-4 py-2 hover:bg-gray-100', unread ? 'opacity-100' : 'opacity-50')}
    >
        <div className='mb-2 flex items-center gap-1 text-sm text-gray-500'>
            <FontAwesomeIcon icon={titleIcon} />
            {title}
            {warehouseName && (
                <Fragment>
                    <Divider orientation='vertical' />
                    <FontAwesomeIcon icon={faWarehouse} />
                    <span data-testid={dataTestid && `${dataTestid}-warehousename`}>{warehouseName}</span>
                </Fragment>
            )}
        </div>
        <div className='relative mb-3 flex items-center gap-3'>
            {unread && (
                <div
                    data-testid={dataTestid && `${dataTestid}-badge`}
                    data-after=''
                    className={clsx([
                        'absolute -left-3 top-1/2 -mt-1',
                        'size-2',
                        'bg-red-500',
                        'rounded-full',
                        'after:content-[attr(data-after)]',
                    ])}
                />
            )}
            {user?.role === 'SERVICE' ? (
                <Avatar
                    dataTestid={dataTestid && `${dataTestid}-avatar`}
                    user={user}
                    showAsCompany
                />
            ) : (
                <Avatar
                    dataTestid={dataTestid && `${dataTestid}-avatar`}
                    user={user}
                />
            )}
            <span className='text-xs text-gray-600'>{formatRelativeDate(receivedAt)}</span>
        </div>
        <div
            data-testid={dataTestid && `${dataTestid}-content`}
            className='text-sm'
        >
            {children}
        </div>
    </div>
);
